import { forwardRef } from 'react'
import * as RadixSwitch from '@radix-ui/react-switch'
import { cn } from '../../lib/cn.js'
import { Text } from './Typography.js'

export interface SwitchProps extends React.ComponentPropsWithoutRef<typeof RadixSwitch.Root> {
  label?: React.ReactNode
  description?: React.ReactNode
}

/**
 * A setting that takes effect as it is changed.
 *
 * Not a checkbox: a checkbox is answered and then submitted, a switch **is** the
 * change. The product's settings screens have no Save for exactly this reason —
 * *"kept as you change it, the way every other setting in the product is"* — so a
 * switch that needs a Save button beside it is the wrong control.
 */
export const Switch = forwardRef<React.ElementRef<typeof RadixSwitch.Root>, SwitchProps>(
  function Switch({ className, label, description, ...props }, ref) {
    const control = (
      <RadixSwitch.Root
        ref={ref}
        className={cn(
          /**
           * The track is 24px tall and stays that way — it is the right size
           * for the thing. What grows is what a finger can hit.
           *
           * `before:` draws an invisible box over the switch reaching out to
           * a 40px height, so the target is a thumb's without the switch
           * getting fatter or the table row getting taller. A switch sitting
           * on its own in a table cell has no label row to tap, unlike the
           * ones in a form — twenty-four of them on the notifications tab
           * alone, each 24px of target.
           */
          'before:absolute before:-inset-y-2 before:-inset-x-1 before:content-[""]',
          'relative h-6 w-11 shrink-0 rounded-full border-2 border-transparent bg-hairline-strong',
          'outline-none transition-colors focus-visible:ring-2 focus-visible:ring-coral-fill/30',
          'data-[state=checked]:bg-coral-fill',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          className,
        )}
        {...props}
      >
        <RadixSwitch.Thumb className="block size-5 rounded-full bg-surface shadow transition-transform data-[state=checked]:translate-x-5" />
      </RadixSwitch.Root>
    )
    if (!label) return control
    return (
      <label className="flex cursor-pointer items-start justify-between gap-4">
        <span className="flex flex-col gap-0.5">
          <Text as="span" variant="body">
            {label}
          </Text>
          {description && (
            <Text as="span" variant="caption">
              {description}
            </Text>
          )}
        </span>
        {control}
      </label>
    )
  },
)
